import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Grid,
  Paper,
  useTheme,
  useMediaQuery,
  CssBaseline,
  Drawer,
  AppBar,
  Toolbar
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { useNavigate } from 'react-router-dom';
import MemberTableView from './MemberTableView';
import MemberTileView from './MemberTileView';
import { retrieveGymMember } from '../Apihelper/MemberApiHelpr';
import Header from '../Includes/Header';
import { useMenuLayout } from '../../contexts/MenuLayoutContext';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';

function MemberListContainer(props) {
  const { colors: appColors } = useAppTheme();
  const navigate = useNavigate();
  const theme = useTheme();
  const { sidebarWidth } = useMenuLayout();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { window1 } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  
  const [members, setMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'tile'
  const [isLoading, setIsLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const fetchMembers = async (page = 1, search = '') => {
    setIsLoading(true);
    setHasMore(true);
    try {
      const userData = localStorage.getItem("userdata");
      const userDataObj = JSON.parse(userData);
      const userToken = userDataObj.token;
      const userId = userDataObj.id;
      const searchText = search;
      const limit = 10;
      const index = (page - 1) * limit;

      const res = await retrieveGymMember(userToken, userId, searchText, limit, index);

      if (res.code == "200") {
        if (page === 1) {
          setMembers(res.data);
        } else {
          setMembers(prev => [...prev, ...res.data]);
        }
        setHasMore(res.data.length === limit);
      } else {
        console.error('API Error:', res.message);
        setMembers([]);
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
      setMembers([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
    fetchMembers(1, value);
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchMembers(nextPage, searchQuery);
  };

  const handleStatusChange = () => {
    setCurrentPage(1);
    fetchMembers(1, searchQuery);
  };

  const handleDelete = () => {
    setCurrentPage(1);
    fetchMembers(1, searchQuery);
  };

  const toggleViewMode = () => {
    setViewMode(prev => prev === 'table' ? 'tile' : 'table');
  };

  const container = window1 !== undefined ? () => window1().document.body : undefined;

  return (
    <ThemeProvider theme={theme}>
    <Box sx={{ display: 'flex', backgroundColor: "#EFEDF1" }}>
      <CssBaseline />
    
        <Box>
        <Header />
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${sidebarWidth}px)` },
          mt: '64px'
        }}
      >
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 2,
            background: 'linear-gradient(to right, #ffffff, #f8fafc)'
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h4" 
                sx={{ 
                  color: appColors.leaf,
                  fontWeight: 'bold',
                  fontSize: { xs: '1.5rem', md: '2rem' }
                }}
              >
                Member List
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <TextField
                  size="small"
                  placeholder="Search members..."
                  value={searchQuery}
                  onChange={handleSearch}
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#ffffff',
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#64748b' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <IconButton
                  onClick={() => setFilterOpen(!filterOpen)}
                  sx={{
                    backgroundColor: '#f1f5f9',
                    '&:hover': { backgroundColor: '#e2e8f0' }
                  }}
                >
                  <FilterListIcon />
                </IconButton>
                <IconButton
                  onClick={toggleViewMode}
                  sx={{
                    backgroundColor: '#f1f5f9',
                    '&:hover': { backgroundColor: '#e2e8f0' }
                  }}
                >
                  {viewMode === 'table' ? <ViewModuleIcon /> : <ViewListIcon />}
                </IconButton>
                <Button
                  variant="contained"
                  onClick={() => navigate('/newmember')}
                  sx={{
                    backgroundColor: appColors.leaf,
                    '&:hover': { backgroundColor: appColors.pine },
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 'bold',
                    display: { xs: 'none', sm: 'block' }
                  }}
                >
                  Add Member
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {viewMode === 'table' ? (
          <MemberTableView
            members={members}
            onLoadMore={handleLoadMore}
            hasMore={hasMore}
            onStatusChange={handleStatusChange}
            onDelete={handleDelete}
            isLoading={isLoading}
          />
        ) : (
          <MemberTileView
            members={members}
            onLoadMore={handleLoadMore}
            hasMore={hasMore}
            onStatusChange={handleStatusChange}
            onDelete={handleDelete}
            isLoading={isLoading}
          />
        )}
      </Box>
    </Box>
    </ThemeProvider>
  );
}

export default MemberListContainer; 